/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { baseApiService } from './base'

interface EosStatusParams {
  activityTypes?: string
  level?: string
  studyFolderId?: number
  tenantUuid?: string
  downloadPhaseStatus?: string
  export?: boolean
  filterOnlyStudyFolder: boolean
  acknowledgementStatus?: string
}

interface StudyDtoType {
  name?: string
  mccEnabled?: boolean
  envUuid?: string
  configurationTypeUuid?: string
  uuid?: string
  grantAccessToUsers?: UserType[]
  removeAccessFromUsers?: UserType[]
  sites?: SiteType[]
  folderId?: number
  studyName?: string
  studyUuid?: string
  forStudyAndAllSites?: boolean
}

interface SiteType {
  name?: string
  rcmFolderId?: number
  uuid?: string
  grantAccessToUsers?: UserType[]
  removeAccessFromUsers?: UserType[]
  folderId?: number
  siteName?: string
  mccEnabled?: boolean
  envUuid?: string
  siteFolderId?: number | string
}

interface StartEosRequestType {
  studyGroupUuid?: string
  targetFolderId?: number
  workflowInitiatorId?: number
  level?: string
  rootFolder?: string
  studyDTO?: StudyDtoType
}
interface UserType {
  name: string
  id?: number
  email?: string
  chName?: string
  firstName?: string
  lastName?: string
  uuid?: string
  grantAccess?: boolean
  assignToWorkflow?: boolean
  createDownloadTask?: boolean
  createAcknowledgementTask?: boolean
}

interface AssignedStudiesResponse {
  studyFolderId: number
  studyName: string
  legacy: boolean
}

interface GetSitesUnderStudyResponse {
  siteName: string
  sanitizedSiteName: string
  siteFolderId: number
}

interface EOSReportsExportRequestType {
  countries?: string[] | null
  help_userStudies?: string[]
  help_userStudyCountries?: string[]
  help_userStudySites?: string[]
  level?: string
  siteFolderIds?: number[]
  studyFolderId?: number
  studyFolderIds?: number[]
}

interface StartEosTransferType {
  mccEnabled?: boolean
  studyGroupUuid?: string
  studyUuid?: string
  studyEnvUuid?: string
}

interface RemoveStudyRequestDataType {
  archived?: boolean
  comment?: string
  contentType?: string
  id?: number
  mccEnabled?: boolean
  name?: string
  remoteFolderName?: string
  studyFolderId?: number
  targetFolderName?: string
  targetFolderPath?: string
}

interface UpdateFtpRequestType {
  id?: number
  enabled?: boolean
  host?: string
  name?: string
  port?: number
  remoteFolder?: string
  metadata?: UserFtpData
  frequency?: number
  targetFolder?: number
  targetFolderName?: string
  targetFolderPath?: string
  raveReportName?: string | null
  initiatorUserId?: string | null
  studyFolderId?: number
  implicit?: boolean
  legacyStudy?: boolean
  dataType?: string | null
  contentType?: string | null
  eosTemplateMapDTO?: EosTemplateMapDTO
}

interface UserFtpData {
  user?: string
  password?: string
}

export interface EosTemplateMapDTO {
  eosStudyId?: number
  siteAckTemplateFsiId?: number
  siteAckTemplateFsiName?: string
  sponsorAckTemplateFsiId?: number
  sponsorAckTemplateFsiName?: string
}

interface TasksPageParamsType {
  pageStart?: number
  pageSize?: number
}

interface UserRowType {
  belongsTo?: string
  displayName?: string
  domId?: string
  email?: string
  firstName?: string
  fullName?: string
  isAcknowledgeSelected?: boolean
  isDownloadSelected?: boolean
  isSelected?: boolean
  isUser?: boolean
  lastName?: string
  postprocess?: () => void
  siteRef?: SiteRefType
  taskAcknowledge?: boolean
  taskDownload?: boolean
  uuid?: string
  wasAcknowledgeSelected?: boolean
  wasDownloadSelected?: boolean
  wasLoaded?: boolean
  wasSelected?: boolean
}

interface SiteRefType {
  activeWf?: string
  displayUsers?: UserRowType[]
  domId?: string
  finishedWf?: WorkflowDetailsType
  groupName?: string
  hasNoUsers?: boolean
  inProgress?: boolean
  isCollapsed?: boolean
  isLoading?: boolean
  isStudy?: boolean
  name?: string
  sanitizedName?: string
  users?: UserRowType[]
  usersLoaded?: boolean
  usersRequest?: Promise<UserType>
  uuid?: string
}

interface WorkflowDetailsType {
  activeWorkflowStep?: string
  folderId?: number
  folderName?: string
  formattedWorkflowId?: string
  status?: string
  workflowId?: string
}

interface GetAckDocTemplateRequestType {
  ftpContextId?: number
  studyFolderId?: number
}

interface UpdatePdfGenStudyRequestDto {
  eosTemplateMapDTO: {
    eosStudyId: number
    siteAckTemplateFsiId: number
    siteAckTemplateFsiName: string
    sponsorAckTemplateFsiId: number
    sponsorAckTemplateFsiName: string
  }
}

interface WorkflowInstructionsExistRequestType {
  studyFolderId: number
}

interface ModifySubmittedWorkflowRequestType {
  uuid?: string
  lastUpdatedAt?: number
  comment?: string
}

interface PdrPublishRequestType {
  documentIds?: number[]
  siteFolderIds?: number[]
  studyFolderId?: number
}

class EosApiService {
  // async getEosStatus(params: EosStatusParams, isSilent: boolean) {
  async getEosStatus(params: EosStatusParams) {
    return baseApiService.fetch('/api/eosdashboard', {
      method: 'GET',
      params: { ...params }
    })
  }

  async getEosStatusDocuments(params: EosStatusParams) {
    return baseApiService.fetch('/api/eosdashboard/folders', {
      method: 'GET',
      params: { ...params }
    })
  }

  async getPlinthStudyGroups() {
    return baseApiService.fetch('/api/eos/plinth/cds')
  }

  async getWorkflowUsers(workflowParentFolderId: number) {
    return baseApiService.fetch(`/api/v2/eos/picker/${workflowParentFolderId}/users`).then((data) => {
      const dataLocal = data as UserType[]
      return dataLocal
        ?.sort((prev, next) => prev?.name?.toLowerCase().localeCompare(next?.name?.toLowerCase()))
        .map((item) => {
          // USE POLICIES TO HIDE/SHOW EMAIL ADDRESS
          item.chName = item.name
          return item
        })
    })
  }

  async getStudyGroups() {
    return baseApiService.fetch('/api/eos/studygroup')
  }

  async getStudyGroup(studyGroupId: string, mccEnabled: boolean) {
    return baseApiService.fetch('/api/eos/studygroup/' + studyGroupId, {
      method: 'GET',
      params: {
        mccEnabled: !!mccEnabled
      }
    })
  }

  async getPlinthStudyEnvironments(studyId: string) {
    return baseApiService.fetch('/api/eos/plinth/studies/' + studyId + '/environments/')
  }

  async getAllUsersForStudy(studyUuid: string, mccEnabled: boolean, folderId: number, studyName: string) {
    return baseApiService.fetch('/api/v1/eos/study/' + studyUuid + '/allusers', {
      method: 'GET',
      params: {
        folderId: folderId,
        mccEnabled: mccEnabled,
        studyName: studyName
      }
    })
  }

  async getUsersForSite(siteUuid: string, params: SiteType) {
    return baseApiService.fetch('/api/v2/eos/sites/' + siteUuid + '/users', {
      method: 'GET',
      params: { ...params } as unknown as Record<string, string | number | boolean | undefined>
    })
  }

  async getUsersForStudyLight(studyUuid: string, params: StudyDtoType) {
    return baseApiService.fetch('/api/v3/eos/study/' + studyUuid + '/allusers', {
      method: 'GET',
      params: { ...params } as unknown as Record<string, string | number | boolean | undefined>
    })
  }

  async getSitesForStudy(studyUuid: string, isMccEnabled: boolean, folderId: number) {
    return baseApiService.fetch('/api/eos/study/' + studyUuid + '/sites', {
      method: 'GET',
      params: {
        folderId: folderId,
        mccEnabled: isMccEnabled
      }
    })
  }

  async getCountriesForStudy(folderId: number) {
    return baseApiService.fetch('/api/eos/internal/study/' + folderId + '/countries')
  }

  async getSitesInCountries(studyUuid: string, folderId: number, isMccEnabled: boolean, countriesList: string[]) {
    return baseApiService.fetch('/api/eos/study/' + studyUuid + '/sites', {
      method: 'GET',
      params: {
        folderId: folderId,
        mccEnabled: isMccEnabled,
        countries: countriesList
      }
    })
  }

  async getSiteForStudy(userUuid: string, studyUuid: string) {
    return baseApiService.fetch('/api/eos/user/' + userUuid + '/study/' + studyUuid)
  }

  async getStudyInfo(ftpContextId: number, studyFolderId: number) {
    return baseApiService.fetch('/api/eos/study/preselect', {
      method: 'GET',
      params: {
        ftpContextId: ftpContextId,
        studyFolderId: studyFolderId
      }
    })
  }

  async downloadMedia(folderIds: number[], studyId: number | null) {
    const reqPayload = {
      ids: folderIds,
      studyId: studyId
    }
    return baseApiService.fetch('/api/eos/download', {
      method: 'POST',
      body: reqPayload
    })
  }

  async getEosFolderId() {
    return baseApiService.fetch('/api/eos/rootfolder')
  }

  async startEos(reqobj: StartEosRequestType) {
    return baseApiService.fetch('/api/eos/starteos', {
      method: 'POST',
      body: { ...reqobj }
    })
  }

  async startEosTransfer(id: number, params: StartEosTransferType) {
    return baseApiService.fetch('/api/eos/transfer/' + id, {
      method: 'GET',
      params: { ...params }
    })
  }

  async getAssignedStudies() {
    return baseApiService.fetch('/api/eos/internal/studies').then((resp) => {
      const respLocal = resp as AssignedStudiesResponse[]
      return respLocal.sort((prev, next) => prev.studyName.toLowerCase().localeCompare(next.studyName.toLowerCase()))
    })
  }

  async getSitesUnderStudy(id: number, countries: string[] | null) {
    // Filter out null values from countries array (matches legacy behavior)
    const filteredCountries = countries?.filter((c): c is string => c != null) ?? undefined
    return baseApiService
      .fetch('/api/eos/internal/study/' + id + '/sites', {
        method: 'GET',
        params: {
          countries: filteredCountries
        }
      })
      .then((resp) => {
        const respLocal = resp as GetSitesUnderStudyResponse[]
        return respLocal.sort((prev, next) => prev.siteName.toLowerCase().localeCompare(next.siteName.toLowerCase()))
      })
  }

  async getCountriesUnderStudy(id: number) {
    return baseApiService.fetch('/api/eos/internal/study/' + id + '/countries')
  }

  async checkPowerUserHasStudy(studyFolderId: number, email: string) {
    return baseApiService.fetch('/api/eos/study/' + studyFolderId + '/users/' + encodeURIComponent(email) + '/present')
  }

  async getUserStudies(data: TasksPageParamsType) {
    const queryParams = { pageStart: 0, pageSize: 10, ...data }
    return baseApiService.fetch('/api/v2/eos/studies', {
      method: 'GET',
      params: { ...queryParams }
    })
  }

  async exportDownloadActivity(reqObj: EOSReportsExportRequestType) {
    return baseApiService.fetch('/api/eos/report/downloadactivity/export', {
      method: 'POST',
      body: {
        filter: reqObj
      }
    })
  }

  async exportAcknowledgementActivity(reqObj: EOSReportsExportRequestType) {
    return baseApiService.fetch('/api/eos/report/acknowledgementactivity/export', {
      method: 'POST',
      body: {
        filter: reqObj
      }
    })
  }

  async generateWorkflowReport(reqObj: EOSReportsExportRequestType) {
    return baseApiService.fetch('/api/eos/report/workflows/export', {
      method: 'POST',
      body: {
        filter: reqObj
      }
    })
  }

  async getFolderWfs(folderId: number, studyUuid: string) {
    return baseApiService.fetch('/api/eos/' + folderId + '/workflows/' + studyUuid)
  }

  async remove(data: RemoveStudyRequestDataType) {
    const pathInLocalStorage =
      data?.remoteFolderName && data?.remoteFolderName.charAt(0) === '/'
        ? data?.remoteFolderName?.substr(1)
        : data?.remoteFolderName
    const requestDto = {
      pathInLocalStorage: pathInLocalStorage,
      ftpContextId: data?.id,
      studyName: data?.name,
      comment: data?.comment ? data.comment : '-'
    }
    // HOW TO USE { 'Content-Type': 'application/json;charset=UTF-8' } HERE
    // return Api.one('/api/eos').customDELETE(null, '', { 'Content-Type': 'application/json;charset=UTF-8' }, requestDto);
    return baseApiService.fetch('/api/eos', {
      method: 'DELETE',
      body: { ...requestDto }
    })
  }

  async updateCountryInfo(studyFolderId: number, isMccEnabled: boolean) {
    return baseApiService.fetch('/api/v2/eos/study/' + studyFolderId + '/countries/fetch', {
      method: 'GET',
      params: {
        mccEnabled: isMccEnabled
      }
    })
  }

  async updateStudySettings(ftpContextId: number, data: UpdateFtpRequestType) {
    return baseApiService.fetch('/api/eos/' + ftpContextId, {
      method: 'PUT',
      body: { ...data }
    })
  }

  // async updateSiteFolderName(fsiId: number, name: string, showLoader: boolean) {
  async updateSiteFolderName(fsiId: number, name: string) {
    const params = {
      name: name
    }
    return baseApiService.fetch('/api/v2/eos/site/' + fsiId + '/rename', {
      method: 'PATCH',
      body: { ...params }
    })
  }

  async getUserTasks(data: TasksPageParamsType) {
    const queryParams = {
      pageStart: 0,
      pageSize: 2,
      ...data
    }
    return baseApiService.fetch('/api/v2/eos/tasks', {
      method: 'GET',
      params: { ...queryParams }
    })
  }

  async getUserDetails(data: StudyDtoType, users: UserRowType[]) {
    const usersObject = Object.fromEntries(
      users.reduce(
        (mem: [string, string | undefined][], user, idx) => {
          mem.push(['users[' + idx + '].userEmail', user?.email])
          mem.push(['users[' + idx + '].userUuid', user?.uuid])
          return mem
        },
        [] as [string, string | undefined][]
      )
    )
    const reqParams = { ...usersObject, ...data }
    return baseApiService.fetch('/api/v3/eos/country/userdetails', {
      method: 'GET',
      params: { ...reqParams } as unknown as Record<string, string | number | boolean | undefined>
    })
  }

  async getUserSites(data: TasksPageParamsType) {
    const queryParams = { pageStart: 0, pageSize: 10, ...data }
    return baseApiService.fetch('/api/v2/eos/sites', {
      method: 'GET',
      params: { ...queryParams }
    })
  }

  async getSiteInfo(folderId: number) {
    return baseApiService.fetch('/api/v2/eos/overview/preselect', {
      method: 'GET',
      params: {
        folderId
      }
    })
  }

  async getRolesValidation(configurationTypeUuid: string) {
    return baseApiService.fetch('/api/v3/eos/validateeosroles/' + configurationTypeUuid)
  }

  async getAckDocTemplateList(params: GetAckDocTemplateRequestType) {
    return baseApiService.fetch('/api/v1/eos/ackDocTemplates', {
      method: 'GET',
      params: { ...params }
    })
  }

  async updatePdfGenStudyData(data: UpdatePdfGenStudyRequestDto, contextId: number) {
    return baseApiService.fetch('/api/v1/pdfgen/update/' + contextId, {
      method: 'PUT',
      body: { ...data }
    })
  }

  async saveUpdateWorkflowinstruction(reqObj: StartEosRequestType) {
    return baseApiService.fetch('/api/v1/workflowinstruction', {
      method: 'POST',
      body: { ...reqObj }
    })
  }

  async checkIfDraftWorkflowinstructionExists(reqObj: WorkflowInstructionsExistRequestType) {
    return baseApiService.fetch('/api/v1/workflowinstruction/exists', {
      method: 'GET',
      params: { ...reqObj }
    })
  }

  async getSavedWorkflowinstruction(params: WorkflowInstructionsExistRequestType) {
    return baseApiService.fetch('/api/v1/workflowinstruction/draft', {
      method: 'GET',
      params: { ...params }
    })
  }

  async archiveEosStudy(data: GetAckDocTemplateRequestType) {
    return baseApiService.fetch('/api/v1/eos/archive', {
      method: 'PUT',
      body: { ...data }
    })
  }

  async getStudyWorkflowsCount(studyFolderId: number, status: string) {
    return baseApiService.fetch('/api/v1/eos/workflows/count?studyFolderId=' + studyFolderId + '&status=' + status)
  }

  async getSubmittedWorkflowinstruction(params: ModifySubmittedWorkflowRequestType) {
    return baseApiService.fetch('/api/v1/workflowinstruction/finalized', {
      method: 'GET',
      params: { ...params }
    })
  }

  async revertSubmittedWorkflowinstruction(params: ModifySubmittedWorkflowRequestType) {
    return baseApiService.fetch(
      '/api/v1/workflowinstruction/' + params.uuid + '/revert?comment=' + encodeURIComponent(params?.comment ?? ''),
      {
        method: 'POST'
      }
    )
  }

  async processSubmittedWorkflowinstruction(uuid: string) {
    return baseApiService.fetch('/api/v1/workflowinstruction/' + uuid + '/process', {
      method: 'POST'
    })
  }

  async getUnprocessedStudies() {
    return baseApiService.fetch('/api/v1/workflowinstruction/unprocessed').then((resp) => {
      const respLocal = resp as AssignedStudiesResponse[]
      return respLocal.sort((prev, next) => prev.studyName.toLowerCase().localeCompare(next.studyName.toLowerCase()))
    })
  }

  async exportInstructionReport(studyFolderId: number, filterId: number) {
    const reqObj = {
      filter: {
        studyFolderId: studyFolderId,
        studyName: '',
        userId: 0,
        status: '',
        filterId: filterId ?? undefined
      }
    }
    return baseApiService.fetch('/api/v1/eos/report/workflowInstruction/export', {
      method: 'POST',
      body: { ...reqObj }
    })
  }

  async checkDraftAccess(studyFolderId: number, userId: number) {
    return baseApiService.fetch(
      '/api/v1/workflowinstruction/checkDraftAccess?studyFolderId=' + studyFolderId + '&userId=' + userId,
      {
        method: 'POST'
      }
    )
  }

  async getConcurrentUser(studyFolderId: number) {
    return baseApiService.fetch('/api/workflowinstruction/concurrentUser?studyFolderId=' + studyFolderId)
  }

  async getPdrStudies() {
    return baseApiService.fetch('/api/v1/pdr/studies')
  }

  async getPdrSites(studyFolderId: number) {
    return baseApiService.fetch('/api/v1/pdr/sites?studyFolderId=' + studyFolderId)
  }

  async getPdrDocuments(searchString: string, page: number, perPage: number, sites: number[]) {
    return baseApiService.fetch(
      searchString
        ? '/api/v1/pdr/documents?page=' +
            page +
            '&perPage=' +
            perPage +
            '&searchText=' +
            encodeURIComponent(searchString)
        : '/api/v1/pdr/documents?page=' + page + '&perPage=' + perPage,
      {
        method: 'POST',
        body: {
          siteFolderIds: sites
        }
      }
    )
  }

  async publishToMyMedidata(requestObj: PdrPublishRequestType) {
    return baseApiService.fetch('/api/v1/pdr/publish', {
      method: 'POST',
      body: { ...requestObj }
    })
  }

  async getSubmittedWorkflowinstructions() {
    return baseApiService.fetch('/api/v1/workflowinstruction/finalizedWFInstructionDetails')
  }
}

export const eosApiService = new EosApiService()
